import React, { FC, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  IconButton,
  DialogContent,
  Button,
  DialogActions,
  Chip,
  Stack,
  TableContainer,
  Card,
  TextField
} from '@mui/material';
import { ZoomIn } from '@mui/icons-material';
import LogoSpinner from 'src/components/LogoSpinner';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';

import {
  DynamicTable,
  TableSearch
} from '../../../components/DynamicTable/DynamicTable';
import { DiscountCode } from './discounts.interface';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import dayjs, { Dayjs } from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import toast from 'react-hot-toast';
import { useDiscountAPI } from '../../../hooks/useDiscountAPI';

const TABLE_TEXT_STYLE = {
  p: '2%',
  height: '100%',
  display: 'flex',
  alignItems: 'center'
};

interface DiscountsContentProps {
  discounts: DiscountCode[];
  loading: boolean;
  setDiscounts: React.Dispatch<React.SetStateAction<DiscountCode[]>>;
  currentDiscount: DiscountCode | null;
  setCurrentDiscount: React.Dispatch<React.SetStateAction<DiscountCode | null>>;
  updateDiscountStatus?: (
    discountId: string,
    newStatus: string
  ) => Promise<void>;
  page: number;
  rowsPerPage: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  setRowsPerPage: React.Dispatch<React.SetStateAction<number>>;
  totalDiscounts: number;
  setTotalDiscounts: React.Dispatch<React.SetStateAction<number>>;
  onRefresh?: () => void;
}

const selectDiscount = (discount) => {};

const renderPillCount = (discounts: DiscountCode[]) => {
  const statusMap = new Map<string, number>();
  discounts.forEach((discount) => {
    const status = discount.appliesTo;
    if (statusMap.has(status)) {
      statusMap.set(status, statusMap.get(status) + 1);
    } else {
      statusMap.set(status, 1);
    }
  });
  const set = new Set<string>(discounts.map((discount) => discount.appliesTo));

  return (
    <>
      {Array.from(set).map((status) => (
        <Box
          key={status}
          sx={{
            display: 'flex',
            gap: '4px',
            alignItems: 'center'
          }}
        >
          <Typography variant="body2">{status.replace(/_/g, ' ')}</Typography>
          <Box
            sx={{
              backgroundColor: '#e0e0e0',
              borderRadius: '12px',
              padding: '2px 8px',
              fontSize: '0.75rem',
              lineHeight: 'normal'
            }}
          >
            {statusMap.get(status)}
          </Box>
        </Box>
      ))}
    </>
  );
};

export const DiscountsContent: FC<DiscountsContentProps> = ({
  discounts,
  loading,
  setDiscounts,
  currentDiscount,
  setCurrentDiscount,
  updateDiscountStatus,
  page,
  rowsPerPage,
  setPage,
  setRowsPerPage,
  totalDiscounts,
  setTotalDiscounts,
  onRefresh
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredDiscounts, setFilteredDiscounts] = useState<DiscountCode[]>(
    []
  );
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedDiscount, setSelectedDiscount] = useState<DiscountCode | null>(
    null
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [discountToDelete, setDiscountToDelete] = useState<string | null>(null);

  const [startDateEditable, setStartDateEditable] = useState(false);
  const [endDateEditable, setEndDateEditable] = useState(false);
  const [formDirty, setFormDirty] = useState(false);
  const {
    updateDiscountCode,
    loading: discountLoading,
    getDiscountCodeById
  } = useDiscountAPI();

  useEffect(() => {
    setFormDirty(false); // Fix initial rendering
  }, []);

  // Filter discounts based on search query
  useEffect(() => {
    if (!searchQuery) {
      setFilteredDiscounts(discounts);
    } else {
      const filtered = discounts.filter(
        (discount) =>
          discount.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
          discount.appliesTo
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          discount.assignedTo.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredDiscounts(filtered);
    }
  }, [discounts, searchQuery]);

  useEffect(() => {
    console.log(
      'startDateEditable or endDateEditable changed:',
      startDateEditable,
      endDateEditable
    );
    if (startDateEditable || endDateEditable) {
      if (!formDirty) {
        setFormDirty(true);
      }
    }
  }, [startDateEditable, endDateEditable]);

  const handleUpdateForm = () => {
    const dayJSStartsAt = selectedDiscount?.startsAt
      ? dayjs(selectedDiscount.startsAt)
      : null;
    const dayJSEndsAt = selectedDiscount?.endsAt
      ? dayjs(selectedDiscount.endsAt)
      : null;
    // If end date is before start date, show error and return
    if (dayJSStartsAt && dayJSEndsAt && dayJSEndsAt.isBefore(dayJSStartsAt)) {
      toast.error('End date cannot be before start date');
      return;
    }
    updateDiscountCode(selectedDiscount.id, {
      startsAt: dayJSStartsAt,
      endsAt: dayJSEndsAt
    })
      .then(() => {
        toast.success('Discount updated successfully');
      })
      .finally(() => {
        setFormDirty(false);
        setStartDateEditable(false);
        setEndDateEditable(false);
        // Close the modal
        handleCloseDialog();
        // Trigger a refresh in the parent component
        onRefresh();
      });
  };

  const handleViewDiscount = async (discount: DiscountCode) => {
    setOpenDialog(true);
    const currentDiscount = await getDiscountCodeById(discount.id);
    setSelectedDiscount(currentDiscount);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedDiscount(null);
  };

  const formatDate = (dateString: string) => {
    if (dateString === '' || !dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusChip = (appliesTo: string) => {
    const colorMap: Record<
      string,
      'primary' | 'secondary' | 'success' | 'warning'
    > = {
      ALL_VENDORS_ALL_PRODUCTS: 'primary',
      ALL_VENDORS: 'primary',
      ALL_PRODUCTS: 'primary',
      SELECTED_PRODUCTS: 'primary',
      SELECTED_VENDORS_AND_SELECTED_PRODUCTS: 'primary'
    };

    return (
      <Chip
        title={appliesTo.replace(/_/g, ' ')}
        label={appliesTo.replace(/_/g, ' ')}
        color={colorMap[appliesTo] || 'default'}
        size="small"
      />
    );
  };

  const headers = [
    {
      headerName: 'Code',
      field: 'code',
      renderCell: ({ row: discount }) => (
        <Typography sx={TABLE_TEXT_STYLE} variant="body2" fontWeight="bold">
          {discount.code}
        </Typography>
      )
    },
    {
      headerName: 'Value',
      field: 'discountValue',
      renderCell: ({ row: discount }) => (
        <Typography sx={TABLE_TEXT_STYLE} variant="body2">
          {discount.discountValue}
        </Typography>
      )
    },
    {
      headerName: 'Applies To',
      field: 'appliesTo',
      renderCell: ({ row: discount }) => getStatusChip(discount.appliesTo)
    },
    {
      headerName: 'Assigned To',
      field: 'assignedTo',
      renderCell: ({ row: discount }) => (
        <Typography sx={TABLE_TEXT_STYLE} variant="body2">
          {discount.assignedTo.replace(/_/g, ' ')}
        </Typography>
      )
    },
    {
      headerName: 'Usage',
      field: 'usageCount',
      renderCell: ({ row: discount }) => (
        <Typography sx={TABLE_TEXT_STYLE} variant="body2">
          {discount.usageCount}
        </Typography>
      )
    },
    {
      headerName: 'Start Date',
      field: 'startsAt',
      renderCell: ({ row: discount }) => (
        <Typography sx={TABLE_TEXT_STYLE} variant="body2">
          {formatDate(discount.startsAt)}
        </Typography>
      )
    },
    {
      headerName: 'End Date',
      field: 'endsAt',
      renderCell: ({ row: discount }) => {
        console.log(discount.endsAt);
        return (
          <Typography sx={TABLE_TEXT_STYLE} variant="body2">
            {formatDate(discount.endsAt)}
          </Typography>
        );
      }
    },
    {
      headerName: 'Actions',
      field: 'actions',
      renderCell: ({ row: discount }) => (
        <Box
          sx={{
            display: 'flex',
            gap: 0,
            p: '2%',
            height: '100%',
            alignItems: 'center'
          }}
        >
          <IconButton
            size="small"
            onClick={() => handleViewDiscount(discount.discount)}
            color="primary"
          >
            <ZoomIn fontSize="small" />
          </IconButton>
        </Box>
      )
    }
  ];

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignContent: 'center',
          py: '5%'
        }}
      >
        <LogoSpinner />
      </Box>
    );
  }

  return (
    <>
      <Card>
        <TableContainer>
          <DynamicTable
            showPagination={true}
            rowsPerPage={rowsPerPage}
            page={page}
            totalPage={totalDiscounts}
            searchElement={
              <TableSearch
                placeholder="Search Discounts"
                searchData={discounts}
                setSearchData={setFilteredDiscounts}
              />
            }
            rows={filteredDiscounts.map((discount) => ({
              id: discount.id,
              code: discount.code,
              discountValue: discount.discountValue,
              appliesTo: discount.appliesTo,
              assignedTo: discount.assignedTo,
              usageCount: discount.usageCount,
              startsAt: discount.startsAt,
              endsAt: discount.endsAt,
              discount: discount,
              rowHeight: 80
            }))}
            headers={headers}
            handleChangePage={(event, newPage) => setPage(newPage)}
            handleChangeRowsPerPage={(event) => setRowsPerPage(event)}
          />
        </TableContainer>
      </Card>

      {/* Discount Details Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
          id="create-discount-dialog-title"
        >
          {!discountLoading && (
            <span
              style={{
                fontWeight: 'bold',
                fontSize: '1.2em'
              }}
            >
              View Discount ({selectedDiscount?.code})
            </span>
          )}

          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              color: (theme) => theme.palette.grey[500]
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {discountLoading && (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <LogoSpinner />
            </Box>
          )}
          {!discountLoading && selectedDiscount && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {selectedDiscount?.code}
              </Typography>
              <Box
                sx={{ display: 'grid', gap: 2, gridTemplateColumns: '1fr 1fr' }}
              >
                <Box>
                  <Typography variant="subtitle2">Discount Value</Typography>
                  <Typography variant="body2">
                    {selectedDiscount.discountValue}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2">Usage Count</Typography>
                  <Typography variant="body2">
                    {selectedDiscount.usageCount}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2">Applies To</Typography>
                  <Typography variant="body2">
                    {selectedDiscount.appliesTo.replace(/_/g, ' ')}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2">Assigned To</Typography>
                  <Typography variant="body2">
                    {selectedDiscount.assignedTo.replace(/_/g, ' ')}
                  </Typography>
                </Box>
                <Box
                  display={'flex'}
                  flexDirection={'row'}
                  alignItems={'end'}
                  gap={2}
                >
                  <Box display={'flex'} flexDirection={'column'}>
                    <Typography variant="subtitle2">Start Date</Typography>
                    {!startDateEditable && (
                      <Typography variant="body2">
                        {formatDate(selectedDiscount.startsAt)}
                      </Typography>
                    )}
                    {startDateEditable && (
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          sx={{
                            width: '140px',
                            p: '8px 2px'
                          }}
                          value={dayjs(selectedDiscount.startsAt ?? null)}
                          onChange={(newValue) => {
                            setStartDateEditable(false); // Close the editable form
                            // Handle date change if needed
                            selectedDiscount.startsAt = newValue
                              ? newValue.toISOString()
                              : null;
                            setSelectedDiscount((prevState) => {
                              return {
                                ...prevState,
                                startsAt: newValue
                                  ? newValue.toISOString()
                                  : null
                              };
                            });
                          }}
                        />
                      </LocalizationProvider>
                    )}
                  </Box>
                  <Box alignItems={'center'}>
                    {!startDateEditable && (
                      <a
                        href="#"
                        onClick={(e) => setStartDateEditable(true)}
                        className={'primary-link'}
                      >
                        {' '}
                        Edit
                      </a>
                    )}
                    {startDateEditable && (
                      <a
                        href="#"
                        onClick={(e) => setStartDateEditable(false)}
                        className={'primary-link'}
                      >
                        {' '}
                        Cancel
                      </a>
                    )}
                  </Box>
                </Box>
                <Box
                  display={'flex'}
                  flexDirection={'row'}
                  alignItems={'end'}
                  gap={2}
                >
                  <Box display={'flex'} flexDirection={'column'}>
                    <Typography variant="subtitle2">End Date</Typography>
                    {!endDateEditable && (
                      <Typography variant="body2">
                        {formatDate(selectedDiscount.endsAt)}
                      </Typography>
                    )}
                    {endDateEditable && (
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        {console.log(selectedDiscount)}
                        <DatePicker
                          sx={{
                            width: '140px',
                            p: '2px'
                          }}
                          value={dayjs(selectedDiscount.endsAt ?? null)}
                          onChange={(newValue) => {
                            // Close the editable form
                            setEndDateEditable(false);
                            selectedDiscount.endsAt = newValue
                              ? newValue.toISOString()
                              : null;
                            setSelectedDiscount((prevState) => {
                              return {
                                ...prevState,
                                endsAt: newValue ? newValue.toISOString() : null
                              };
                            });
                          }}
                        />
                      </LocalizationProvider>
                    )}
                  </Box>
                  <Box alignItems={'center'}>
                    {!endDateEditable && (
                      <a
                        href="#"
                        onClick={(e) => setEndDateEditable(true)}
                        className={'primary-link'}
                      >
                        {' '}
                        Edit
                      </a>
                    )}
                    {endDateEditable && (
                      <a
                        href="#"
                        onClick={(e) => setEndDateEditable(false)}
                        className={'primary-link'}
                      >
                        {' '}
                        Cancel
                      </a>
                    )}
                  </Box>
                </Box>
              </Box>

              {selectedDiscount.assignedProducts &&
                selectedDiscount.assignedProducts.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Assigned Products
                    </Typography>
                    <Box
                      sx={{ mt: 1, gap: 1, display: 'flex', flexWrap: 'wrap' }}
                    >
                      {selectedDiscount.assignedProducts.map((product) => (
                        <Chip
                          label={product.productDescription}
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Box>
                )}

              {selectedDiscount.vendors && selectedDiscount.vendors.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Vendors
                  </Typography>
                  {selectedDiscount.vendors.map((vendor) => (
                    <Typography key={vendor.vendorId} variant="body2">
                      {vendor.vendorName}
                    </Typography>
                  ))}
                </Box>
              )}

              {selectedDiscount.assignedVendors &&
                selectedDiscount.assignedVendors.length > 0 && (
                  <>
                    <Typography sx={{ mt: 2 }} variant="subtitle2" gutterBottom>
                      Assigned Vendors
                    </Typography>
                    <Box
                      sx={{ mt: 1, gap: 1, display: 'flex', flexWrap: 'wrap' }}
                    >
                      {selectedDiscount.assignedVendors.map((vendor) => (
                        <Chip label={vendor.vendorName} variant="outlined" />
                      ))}
                    </Box>
                  </>
                )}

              {selectedDiscount.assignedBuyers &&
                selectedDiscount.assignedBuyers.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      <strong>Discount Code used by Buyers</strong>
                    </Typography>
                    <Box
                      sx={{
                        gap: 0.5,
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'wrap'
                      }}
                    >
                      {selectedDiscount.assignedBuyers.map((buyer, index) => (
                        <Box
                          key={index}
                          sx={{
                            border: '1px solid #d8d8d8',
                            background: '#5770b5',
                            color: 'white',
                            p: '0.8%',
                            width: 'fit-content',
                            borderRadius: '5px'
                          }}
                        >
                          {buyer.buyerFullName} ({buyer.buyerEmail})
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {formDirty && (
            <Button
              onClick={handleUpdateForm}
              variant="contained"
              color="primary"
              disabled={discountLoading}
              loading={discountLoading ? true : false}
            >
              Save Changes
            </Button>
          )}
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
